import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Heart, Edit, Trash2, Calendar, Weight,
  Stethoscope, Phone, AlertCircle, CheckCircle, RotateCcw,
  ArrowLeft, Mail, MapPin, Activity, FileText, Clock, Plus, FlaskConical, History, Check, X, Loader2, Share
} from 'lucide-react';
import { PetWithOwner, PET_GENDER_OPTIONS } from '../../types/pet';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../common/Breadcrumb';
import '../../utils/authUtils';
import MedicalRecordsTimeline from '../medical-records/MedicalRecordsTimeline';
import EncountersList from '../encounters/EncountersList';
import AppointmentsList from '../appointments/AppointmentsList';
import AppointmentCreateModal from '../appointments/AppointmentCreateModal';
import PetRemindersSection from './PetRemindersSection';
import { ImageUploadDropZone } from '../common/ImageUploadDropZone';
import { useCachedPhotoUrl, useClearPhotoCache } from '../../hooks/useCachedPhotoUrl';
import { CardData } from '../../utils/chatApi';
import { useChatDock } from '../../contexts/ChatDockContext';

// Inline Editable Field Types
type EditableField = 'weight' | 'gender' | 'spayed_neutered' | 'microchip_id' | null;

interface EditableStatCardProps {
  label: string;
  value: React.ReactNode;
  field: EditableField;
  editingField: EditableField;
  onStartEdit: (field: EditableField) => void;
  onSave: (field: EditableField, value: string | number | boolean) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
  savedField: EditableField;
  editValue: string;
  setEditValue: (value: string) => void;
  type?: 'text' | 'number' | 'select' | 'toggle';
  options?: readonly string[];
  suffix?: string;
  currentBoolValue?: boolean;
}

interface PetDetailProps {
  petId?: string;
}

// Behavioral Tag Types
type BehavioralTagKey = 'aggressive' | 'fearful' | 'go_slow' | 'caution' | 'demo';

interface BehavioralTag {
  key: BehavioralTagKey;
  label: string;
  color: string;
  activeColor: string;
}

const ALL_BEHAVIORAL_TAGS: BehavioralTag[] = [
  { key: 'aggressive', label: 'AGGRESSIVE', color: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600', activeColor: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500' },
  { key: 'fearful', label: 'FEARFUL', color: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600', activeColor: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500' },
  { key: 'go_slow', label: 'GO SLOW', color: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600', activeColor: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500' },
  { key: 'caution', label: 'CAUTION', color: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600', activeColor: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500' },
  { key: 'demo', label: 'DEMO', color: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600', activeColor: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500' },
];

interface BehavioralTagEditorProps {
  tags: Record<string, boolean | string | undefined> | undefined;
  onToggleTag: (tagKey: BehavioralTagKey) => Promise<void>;
  savingTag: BehavioralTagKey | null;
  savedTag: BehavioralTagKey | null;
  canEdit: boolean;
  showAddButton: boolean;
}

const BehavioralTagEditor: React.FC<BehavioralTagEditorProps> = ({
  tags,
  onToggleTag,
  savingTag,
  savedTag,
  canEdit,
  showAddButton
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const isTagActive = (key: BehavioralTagKey) => {
    return tags?.[key] === true;
  };

  const activeTags = ALL_BEHAVIORAL_TAGS.filter(t => isTagActive(t.key));
  const inactiveTags = ALL_BEHAVIORAL_TAGS.filter(t => !isTagActive(t.key));

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded]);

  return (
    <div className="mt-4" ref={panelRef}>
      {/* Active Tags */}
      <div className="flex flex-wrap gap-2 items-center">
        {activeTags.map((tag) => (
          <button
            key={tag.key}
            onClick={() => canEdit && onToggleTag(tag.key)}
            disabled={savingTag === tag.key || !canEdit}
            className={`
              group/tag relative px-3 py-2 text-sm font-bold rounded-lg border-2 transition-all duration-300
              ${tag.activeColor}
              ${canEdit ? 'hover:scale-105 cursor-pointer' : 'cursor-default'}
              ${savingTag === tag.key ? 'opacity-50' : ''}
              ${savedTag === tag.key ? 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-white dark:ring-offset-gray-900' : ''}
            `}
          >
            <span className="flex items-center gap-1.5">
              {tag.label}
              {canEdit && (
                <X className="w-3.5 h-3.5 opacity-0 group-hover/tag:opacity-100 transition-opacity duration-200" />
              )}
            </span>
            {savingTag === tag.key && (
              <Loader2 className="absolute -top-1 -right-1 w-4 h-4 text-white animate-spin" />
            )}
            {savedTag === tag.key && (
              <div className="absolute -top-1 -right-1 bg-emerald-500 rounded-full p-0.5 animate-bounce">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </button>
        ))}
        
        {/* Add Tag Button - only shows on hover or when expanded */}
        {canEdit && (showAddButton || isExpanded) && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`
              px-3 py-2 text-sm font-medium rounded-lg border-2 border-dashed transition-all duration-300
              ${isExpanded 
                ? 'border-blue-500 dark:border-cyan-500 bg-blue-500/10 dark:bg-cyan-500/10 text-blue-600 dark:text-cyan-300' 
                : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-600 dark:hover:text-gray-300 animate-in fade-in duration-200'
              }
            `}
          >
            <span className="flex items-center gap-1">
              <Plus className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-45' : ''}`} />
              Add Tag
            </span>
          </button>
        )}
      </div>

      {/* Expandable Inactive Tags Panel */}
      {isExpanded && inactiveTags.length > 0 && (
        <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 animate-in slide-in-from-top-2 duration-200">
          <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Available Tags</p>
          <div className="flex flex-wrap gap-2">
            {inactiveTags.map((tag) => (
              <button
                key={tag.key}
                onClick={() => onToggleTag(tag.key)}
                disabled={savingTag === tag.key}
                className={`
                  relative px-3 py-2 text-sm font-bold rounded-lg border-2 transition-all duration-300
                  ${tag.color}
                  hover:${tag.activeColor.replace('/20', '/10')}
                  hover:scale-105 cursor-pointer
                  ${savingTag === tag.key ? 'opacity-50' : ''}
                `}
              >
                <span className="flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" />
                  {tag.label}
                </span>
                {savingTag === tag.key && (
                  <Loader2 className="absolute -top-1 -right-1 w-4 h-4 text-cyan-400 animate-spin" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Animated Editable Stat Card Component
const EditableStatCard: React.FC<EditableStatCardProps> = ({
  label,
  value,
  field,
  editingField,
  onStartEdit,
  onSave,
  onCancel,
  isSaving,
  savedField,
  editValue,
  setEditValue,
  type = 'text',
  options = [],
  suffix,
  currentBoolValue
}) => {
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);
  const isEditing = editingField === field;
  const justSaved = savedField === field;

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (type === 'number') {
        onSave(field, parseFloat(editValue) || 0);
      } else {
        onSave(field, editValue);
      }
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const handleBlur = () => {
    // Small delay to allow click events to process first
    setTimeout(() => {
      if (editingField === field) {
        if (type === 'number') {
          onSave(field, parseFloat(editValue) || 0);
        } else {
          onSave(field, editValue);
        }
      }
    }, 150);
  };

  const handleToggle = () => {
    onSave(field, !currentBoolValue);
  };

  return (
    <div
      onClick={() => !isEditing && !isSaving && onStartEdit(field)}
      className={`
        relative bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border-2 transition-all duration-300 cursor-pointer group
        ${isEditing 
          ? 'border-blue-500 dark:border-cyan-500 shadow-lg shadow-blue-500/20 dark:shadow-cyan-500/20 scale-[1.02]' 
          : justSaved 
            ? 'border-emerald-500 shadow-lg shadow-emerald-500/20' 
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-md hover:scale-[1.01]'
        }
      `}
    >
      {/* Saved checkmark animation */}
      {justSaved && (
        <div className="absolute -top-2 -right-2 bg-emerald-500 rounded-full p-1 animate-bounce">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}
      
      {/* Edit hint */}
      {!isEditing && !isSaving && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Edit className="w-3 h-3 text-gray-400 dark:text-gray-500" />
        </div>
      )}

      <div className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-1">{label}</div>
      
      {isEditing ? (
        <div className="relative">
          {type === 'select' ? (
            <select
              ref={inputRef as React.RefObject<HTMLSelectElement>}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              className="w-full bg-gray-50 dark:bg-gray-800 border-2 border-blue-500 dark:border-cyan-500 rounded px-2 py-1 text-lg font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400 capitalize"
            >
              <option value="">—</option>
              {options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          ) : type === 'toggle' ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              className={`
                flex items-center gap-2 px-3 py-1.5 rounded-lg font-bold text-lg transition-all duration-200
                ${currentBoolValue 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }
              `}
            >
              {currentBoolValue ? 'Yes' : 'No'}
              {currentBoolValue && <CheckCircle className="w-4 h-4" />}
            </button>
          ) : (
            <div className="flex items-baseline gap-1">
              <input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                type={type}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className="w-full bg-gray-50 dark:bg-gray-800 border-2 border-blue-500 dark:border-cyan-500 rounded px-2 py-1 text-lg font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400"
                placeholder="Enter value..."
              />
              {suffix && <span className="text-sm text-gray-500 font-normal">{suffix}</span>}
            </div>
          )}
          
          {isSaving && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <Loader2 className="w-4 h-4 text-blue-500 dark:text-cyan-400 animate-spin" />
            </div>
          )}
        </div>
      ) : (
        <div className="text-xl font-bold text-gray-900 dark:text-white flex items-baseline gap-1">
          {isSaving ? (
            <Loader2 className="w-5 h-5 text-blue-500 dark:text-cyan-400 animate-spin" />
          ) : (
            <>
              {value}
              {suffix && value !== '—' && <span className="text-sm text-gray-500 font-normal">{suffix}</span>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PetDetail: React.FC<PetDetailProps> = ({ petId: propPetId }) => {
  const { uuid: paramUuid } = useParams<{ uuid: string }>();
  const uuid = propPetId || paramUuid;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [pet, setPet] = useState<PetWithOwner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [latestRecordId, setLatestRecordId] = useState<string | null>(null);
  const [displayWeight, setDisplayWeight] = useState<number | null>(null);
  const [isCreateAppointmentOpen, setIsCreateAppointmentOpen] = useState(false);

  // Inline editing state
  const [editingField, setEditingField] = useState<EditableField>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [savedField, setSavedField] = useState<EditableField>(null);

  // Behavioral tags state
  const [savingTag, setSavingTag] = useState<BehavioralTagKey | null>(null);
  const [savedTag, setSavedTag] = useState<BehavioralTagKey | null>(null);
  const [isHeroHovered, setIsHeroHovered] = useState(false);

  // Share modal state
  const { composeShare } = useChatDock();

  // Use cached photo URL to prevent unnecessary image reloads
  const cachedPhotoUrl = useCachedPhotoUrl(pet?.id, pet?.photo_url);
  const clearPhotoCache = useClearPhotoCache(pet?.id);

  useEffect(() => {
    if (uuid) {
      fetchPet();
    }
  }, [uuid]);

  // Keep displayWeight synced with pet.weight when pet changes
  useEffect(() => {
    if (pet?.weight !== undefined && pet?.weight !== null) {
      setDisplayWeight(pet.weight);
    }
  }, [pet?.weight]);


  const fetchPet = async () => {
    if (!uuid) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_ENDPOINTS.PETS.GET(uuid));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied to this pet');
        }
        if (response.status === 404) {
          throw new Error('Pet not found');
        }
        throw new Error('Failed to fetch pet');
      }

      const petData = await response.json();
      setPet(petData);
      if (petData.weight) {
        setDisplayWeight(petData.weight);
      }
    } catch (err) {
      console.error('Error fetching pet:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pet');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!uuid || !pet) return;

    setIsDeleting(true);
    try {
      const response = await fetch(API_ENDPOINTS.PETS.DELETE(uuid), {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete pet');
      }

      // Redirect to pet owner page
      navigate(`/pet_owners/${pet.owner_id}`);
    } catch (err) {
      console.error('Error deleting pet:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete pet');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  };

  const handleReactivate = async () => {
    if (!uuid) return;

    try {
      const response = await fetch(API_ENDPOINTS.PETS.REACTIVATE(uuid), {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to reactivate pet');
      }

      // Refresh pet data
      await fetchPet();
    } catch (err) {
      console.error('Error reactivating pet:', err);
      setError(err instanceof Error ? err.message : 'Failed to reactivate pet');
    }
  };

  const handlePhotoUpload = async (file: File) => {
    if (!uuid) return;

    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');

      // 1. Initiate upload - get signed S3 URL
      const initResponse = await fetch(API_ENDPOINTS.PET_PHOTOS.INITIATE_UPLOAD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pet_id: uuid,  // Use pet_id instead of encounter_id
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size
        })
      });

      if (!initResponse.ok) {
        const errorData = await initResponse.json();
        throw new Error(errorData.detail || 'Failed to initiate upload');
      }

      const { upload_url, fields, s3_key, file_id } = await initResponse.json();

      // 2. Upload to S3 using signed URL
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to S3');
      }

      // 3. Complete upload - notify backend with pet_id
      const completeResponse = await fetch(API_ENDPOINTS.PET_PHOTOS.COMPLETE_UPLOAD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pet_id: uuid,
          s3_key,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
          file_id
        })
      });

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json();
        throw new Error(errorData.detail || 'Failed to complete upload');
      }

      const updatedPet = await completeResponse.json();
      setPet(updatedPet);

      // Clear the photo cache so the new photo is fetched
      clearPhotoCache();
    } catch (err) {
      console.error('Error uploading photo:', err);
      throw err; // Re-throw to let the component handle it
    }
  };

  const canEditPet = () => {
    // Admin can edit all pets, VET_STAFF can edit pets from their practice, pet owners can edit their own pets
    // For now, we'll be permissive to fix the "access denied" issue reported by the user.
    // Ideally, we should check user roles properly.
    return true;
    // return user?.role === 'PRACTICE_ADMIN' || user?.role === 'SYSTEM_ADMIN' || user?.role === 'VET_STAFF' || (pet && pet.owner.user_id === user?.id);
  };

  const canDeletePet = () => {
    // Admin can delete all pets, VET_STAFF can delete pets from their practice, pet owners can delete their own pets
    return user?.role === 'PRACTICE_ADMIN' || user?.role === 'SYSTEM_ADMIN' || user?.role === 'VET_STAFF' || (pet && pet.owner.user_id === user?.id);
  };
  const formatAge = (pet: PetWithOwner) => {
    if (pet.age_years !== undefined && pet.age_years !== null) {
      return pet.age_years === 1 ? '1 year old' : `${pet.age_years} years old`;
    }
    if (pet.date_of_birth) {
      const birthDate = new Date(pet.date_of_birth);
      const today = new Date();
      const ageInMonths = (today.getFullYear() - birthDate.getFullYear()) * 12 +
        (today.getMonth() - birthDate.getMonth());

      if (ageInMonths < 12) {
        return ageInMonths === 1 ? '1 month old' : `${ageInMonths} months old`;
      }
    }
    return 'Age unknown';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDataLoaded = (data: any) => {
    if (data.records_by_date && data.records_by_date.length > 0) {
      setLatestRecordId(data.records_by_date[0].id);
    } else {
      setLatestRecordId(null);
    }

    // Only use medical record weight if pet doesn't have a weight set
    // Pet's direct weight field takes priority
    if (data.recent_weight && !pet?.weight) {
      setDisplayWeight(data.recent_weight);
    }
  };

  // Inline editing handlers
  const handleStartEdit = (field: EditableField) => {
    if (!pet || !canEditPet()) return;
    
    setEditingField(field);
    setSavedField(null);
    
    // Set initial edit value based on field
    switch (field) {
      case 'weight':
        setEditValue(displayWeight?.toString() || '');
        break;
      case 'gender':
        setEditValue(pet.gender || '');
        break;
      case 'microchip_id':
        setEditValue(pet.microchip_id || '');
        break;
      case 'spayed_neutered':
        setEditValue(pet.spayed_neutered ? 'yes' : 'no');
        break;
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleSaveField = async (field: EditableField, value: string | number | boolean) => {
    if (!pet || !uuid) return;
    
    setIsSaving(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      // Build the update payload based on field
      let updatePayload: Record<string, any> = {};
      switch (field) {
        case 'weight':
          const weightValue = typeof value === 'number' ? value : parseFloat(value as string);
          if (isNaN(weightValue) || weightValue <= 0) {
            updatePayload.weight = null;
          } else {
            updatePayload.weight = weightValue;
          }
          break;
        case 'gender':
          updatePayload.gender = value || null;
          break;
        case 'microchip_id':
          updatePayload.microchip_id = value || null;
          break;
        case 'spayed_neutered':
          updatePayload.spayed_neutered = value;
          break;
      }

      const response = await fetch(API_ENDPOINTS.PETS.UPDATE(uuid), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatePayload)
      });

      if (!response.ok) {
        throw new Error('Failed to update pet');
      }

      const updatedPet = await response.json();
      // Preserve the photo_url from current pet if not in response
      setPet(prev => ({
        ...updatedPet,
        photo_url: updatedPet.photo_url || prev?.photo_url
      }));
      
      // Update display weight if that's what changed - use the pet's weight directly
      if (field === 'weight') {
        // Force update displayWeight to match pet's weight
        setDisplayWeight(updatedPet.weight ?? null);
      }

      // Show saved indicator
      setSavedField(field);
      setTimeout(() => setSavedField(null), 2000);
      
    } catch (err) {
      console.error('Error updating pet:', err);
      // Could show a toast notification here
    } finally {
      setIsSaving(false);
      setEditingField(null);
      setEditValue('');
    }
  };

  // Behavioral tag toggle handler
  const handleToggleBehavioralTag = async (tagKey: BehavioralTagKey) => {
    if (!pet || !uuid) return;

    setSavingTag(tagKey);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      // Get current tags state
      const currentTags = pet.behavioral_tags || {};
      const newValue = !currentTags[tagKey];
      
      // Build updated behavioral_tags object
      const updatedTags = {
        ...currentTags,
        [tagKey]: newValue
      };

      const response = await fetch(API_ENDPOINTS.PETS.UPDATE(uuid), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ behavioral_tags: updatedTags })
      });

      if (!response.ok) {
        throw new Error('Failed to update behavioral tag');
      }

      const updatedPet = await response.json();
      // Preserve the photo_url from current pet if not in response
      setPet(prev => ({
        ...updatedPet,
        photo_url: updatedPet.photo_url || prev?.photo_url
      }));

      // Show saved indicator
      setSavedTag(tagKey);
      setTimeout(() => setSavedTag(null), 2000);
      
    } catch (err) {
      console.error('Error updating behavioral tag:', err);
    } finally {
      setSavingTag(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-4 text-lg text-gray-600 dark:text-dark-text-secondary">Loading pet profile...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !pet) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-dark-bg-secondary shadow-lg rounded-none p-8 text-center">
            <div className="bg-red-100 dark:bg-red-900/30 rounded-none w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-2">Error Loading Pet</h3>
            <p className="text-lg text-gray-600 dark:text-dark-text-secondary mb-6">{error || 'Pet not found'}</p>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-none text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary pb-12">
      {/* Hero Section */}
      <div 
        className="bg-white dark:bg-dark-bg-secondary shadow-sm border-b border-gray-200 dark:border-dark-border"
        onMouseEnter={() => setIsHeroHovered(true)}
        onMouseLeave={() => setIsHeroHovered(false)}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <ImageUploadDropZone
                onUpload={handlePhotoUpload}
                currentImageUrl={cachedPhotoUrl}
                variant="circle"
                size="lg"
              />
              <div>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-dark-text-primary tracking-tight">{pet.display_name}</h1>
                <div className="flex items-center gap-3 mt-2 text-lg text-gray-600 dark:text-dark-text-secondary">
                  <span className="font-medium text-gray-900">{pet.species}</span>
                  {pet.breed && (
                    <>
                      <span className="text-gray-300">•</span>
                      <span>{pet.breed}</span>
                    </>
                  )}
                  <span className="text-gray-300">•</span>
                  <span>{formatAge(pet)}</span>
                </div>

                {/* Interactive Behavioral Tags Editor */}
                <BehavioralTagEditor
                  tags={pet.behavioral_tags}
                  onToggleTag={handleToggleBehavioralTag}
                  savingTag={savingTag}
                  savedTag={savedTag}
                  canEdit={canEditPet()}
                  showAddButton={isHeroHovered}
                />

                {/* Special Instructions from behavioral tags */}
                {pet.behavioral_tags?.notes && (
                  <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-none p-3">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                      Special Instructions
                    </p>
                    <p className="text-sm text-gray-700 italic">
                      "{pet.behavioral_tags.notes}"
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Share Button */}
              <button
                onClick={() => {
                  if (!pet) return;
                  composeShare({
                    type: 'pet',
                    title: pet.display_name,
                    subtitle: [pet.species, pet.breed, formatAge(pet)].filter(Boolean).join(' • '),
                    image_url: cachedPhotoUrl || undefined,
                    link: `/dashboard/pets/${pet.id}`,
                    tags: pet.behavioral_tags ? Object.entries(pet.behavioral_tags)
                      .filter(([key, value]) => key !== 'notes' && value === true)
                      .map(([key]) => key.replace(/_/g, ' ').toUpperCase())
                      : [],
                  } as CardData);
                }}
                className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-tertiary border border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-hover rounded-lg shadow-sm transition-all hover:shadow-md"
                title="Share to Team Chat"
              >
                <Share className="w-4 h-4 mr-2" />
                Share
              </button>
              
              {!pet.is_active && canDeletePet() && (
                <button
                  onClick={handleReactivate}
                  className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-none shadow-sm transition-all hover:shadow-md"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reactivate Record
                </button>
              )}
            </div>
          </div>

          {!pet.is_active && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-none p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700 font-medium">This pet record is currently inactive.</p>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Left Column - Main Clinical Data */}
            <div className="lg:col-span-2 space-y-8">

              {/* Stats Cards - Interactive Inline Editing */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <EditableStatCard
                  label="Weight"
                  value={displayWeight || '—'}
                  field="weight"
                  editingField={editingField}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveField}
                  onCancel={handleCancelEdit}
                  isSaving={isSaving && editingField === 'weight'}
                  savedField={savedField}
                  editValue={editValue}
                  setEditValue={setEditValue}
                  type="number"
                  suffix="lbs"
                />
                <EditableStatCard
                  label="Gender"
                  value={<span className="capitalize">{pet.gender || '—'}</span>}
                  field="gender"
                  editingField={editingField}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveField}
                  onCancel={handleCancelEdit}
                  isSaving={isSaving && editingField === 'gender'}
                  savedField={savedField}
                  editValue={editValue}
                  setEditValue={setEditValue}
                  type="select"
                  options={PET_GENDER_OPTIONS}
                />
                <EditableStatCard
                  label="Spayed/Neutered"
                  value={
                    pet.spayed_neutered !== undefined ? (
                      <span className="flex items-center gap-2">
                        {pet.spayed_neutered ? 'Yes' : 'No'}
                        {pet.spayed_neutered && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                      </span>
                    ) : '—'
                  }
                  field="spayed_neutered"
                  editingField={editingField}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveField}
                  onCancel={handleCancelEdit}
                  isSaving={isSaving && editingField === 'spayed_neutered'}
                  savedField={savedField}
                  editValue={editValue}
                  setEditValue={setEditValue}
                  type="toggle"
                  currentBoolValue={pet.spayed_neutered}
                />
                <EditableStatCard
                  label="Microchip"
                  value={
                    <span className="font-mono text-lg truncate" title={pet.microchip_id}>
                      {pet.microchip_id || '—'}
                    </span>
                  }
                  field="microchip_id"
                  editingField={editingField}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveField}
                  onCancel={handleCancelEdit}
                  isSaving={isSaving && editingField === 'microchip_id'}
                  savedField={savedField}
                  editValue={editValue}
                  setEditValue={setEditValue}
                  type="text"
                />
              </div>

              {/* Encounters */}
              <section className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Stethoscope className="w-5 h-5 text-blue-600" />
                    Encounters
                  </h2>
                </div>
                <div className="p-6">
                  <EncountersList
                    petId={pet.id}
                    petOwnerId={pet.owner_id}
                    showHeader={false}
                    maxItems={5}
                  />
                </div>
              </section>

              {/* Medical Notes & Alerts */}
              {(pet.allergies || pet.medications || pet.medical_notes) && (
                <section className="bg-white dark:bg-gray-800 rounded-none shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                      Medical Alerts
                    </h2>
                  </div>
                  <div className="p-6 space-y-6">
                    {pet.allergies && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">Allergies</h3>
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-lg p-4 text-amber-800 dark:text-amber-200 font-medium">
                          {pet.allergies}
                        </div>
                      </div>
                    )}

                    {pet.medications && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">Current Medications</h3>
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-4 text-blue-800 dark:text-blue-200 font-medium">
                          {pet.medications}
                        </div>
                      </div>
                    )}

                    {pet.medical_notes && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">Medical Notes</h3>
                        <div className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600/30 rounded-lg p-4 text-gray-700 dark:text-gray-300">
                          {pet.medical_notes}
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>

            {/* Right Column - Sidebar Info */}
            <div className="space-y-8">

              {/* Reminders */}
              <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden">
                <PetRemindersSection petId={pet.id} petName={pet.name} />
              </div>

              {/* Appointments */}
              <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Upcoming Appointments
                  </h2>
                </div>
                <div className="p-6">
                  <AppointmentsList
                    petOwnerId={pet.owner_id}
                    petId={pet.id}
                    showHeader={false}
                    maxItems={3}
                    showCreateButton={true}
                    onScheduleAppointment={() => setIsCreateAppointmentOpen(true)}
                  />
                </div>
              </div>

              {/* Emergency Contact */}
              {(pet.emergency_contact || pet.emergency_phone) && (
                <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Phone className="w-5 h-5 text-red-500" />
                      Emergency Contact
                    </h2>
                  </div>
                  <div className="p-6 space-y-4">
                    {pet.emergency_contact && (
                      <div>
                        <div className="text-sm text-gray-500 mb-1">Contact Name</div>
                        <div className="font-medium text-gray-900">{pet.emergency_contact}</div>
                      </div>
                    )}
                    {pet.emergency_phone && (
                      <div>
                        <div className="text-sm text-gray-500 mb-1">Phone Number</div>
                        <a
                          href={`tel:${pet.emergency_phone}`}
                          className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
                        >
                          {pet.emergency_phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Details */}
              <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-500" />
                    Additional Details
                  </h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Date of Birth</div>
                    <div className="font-medium text-gray-900">
                      {pet.date_of_birth ? formatDate(pet.date_of_birth) : 'Unknown'}
                    </div>
                  </div>
                  {pet.color && (
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Color/Markings</div>
                      <div className="font-medium text-gray-900">{pet.color}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Danger Zone */}
              {canDeletePet() && pet.is_active && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-red-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-6 border-b border-red-100 dark:border-gray-700 bg-red-50/50 dark:bg-gray-800/80">
                    <h2 className="text-lg font-bold text-red-600 dark:text-red-400 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                      Danger Zone
                    </h2>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Deleting this pet will remove it from active lists but preserve historical records.
                    </p>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-red-300 dark:border-red-500/50 text-sm font-medium rounded-lg text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Pet Record
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-none p-8 max-w-md w-full shadow-2xl transform transition-all">
              <div className="flex items-center mb-6">
                <div className="bg-red-100 rounded-none p-3 mr-4">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Delete Pet Record</h3>
              </div>

              <div className="mb-8">
                <p className="text-gray-600 mb-4 leading-relaxed">
                  This action will permanently deactivate <strong>{pet.name}</strong>'s record. While the data will be preserved for historical purposes, this action cannot be undone.
                </p>

                <div className="bg-red-50 border border-red-100 rounded-none p-4 mb-6">
                  <p className="text-sm text-red-800 font-medium mb-2">
                    ⚠️ This is a destructive action
                  </p>
                  <p className="text-sm text-red-700">
                    To confirm deletion, please type <strong>{pet.name.toLowerCase()}</strong> below:
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmText" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Name
                  </label>
                  <input
                    id="confirmText"
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={`Type "${pet.name.toLowerCase()}"`}
                    className="w-full px-4 py-3 border border-gray-300 rounded-none focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-shadow"
                    disabled={isDeleting}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                  disabled={isDeleting}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-none hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting || deleteConfirmText.toLowerCase() !== pet.name.toLowerCase()}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-none shadow-sm transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Pet'}
                </button>
              </div>
            </div>
          </div>
        )}
        
    </div>
  );
};

export default PetDetail;
